import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { TranslateService } from '@ngx-translate/core';
import { NotificationTemplatesActions } from '@states/notification-templates/notification-templates.actions';
import * as SharedActions from '@states/shared/shared.actions';
import { NotificationTemplatesModels } from '@models/notification-templates.model';
import { NotificationService } from 'src/app/services/notification.service';
import { catchError, exhaustMap, share, switchMap } from 'rxjs';

@Injectable()
export class NotificationTemplatesEffects {
  public loadHistory$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationTemplatesActions.loadHistory),
      switchMap(() =>
        this.notificationService.listSentNotifications().pipe(
          switchMap((history) => [
            NotificationTemplatesActions.loadHistorySuccess({ history }),
          ]),
          catchError((response) => [
            SharedActions.showMessage({ error: response?.error?.message }),
            NotificationTemplatesActions.loadHistoryFail({ error: response?.error?.message ?? null }),
          ]),
        ),
      ),
      share(),
    ),
  );

  public loadTemplates$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationTemplatesActions.loadTemplates),
      switchMap(() =>
        this.notificationService.listEmailTemplates().pipe(
          switchMap((templates) => [
            NotificationTemplatesActions.loadTemplatesSuccess({ templates }),
          ]),
          catchError((response) => [
            SharedActions.showMessage({ error: response?.error?.message }),
            NotificationTemplatesActions.loadTemplatesFail({ error: response?.error?.message ?? null }),
          ]),
        ),
      ),
      share(),
    ),
  );

  public sendNotification$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationTemplatesActions.sendNotification),
      exhaustMap(({ templateSlug, templateData, recipients, groupIds }) => {
        const payload: NotificationTemplatesModels.SendNotificationRequest = {
          templateSlug,
          templateData,
          recipients,
          groupIds,
        };

        return this.notificationService.sendNotification(payload).pipe(
          switchMap((row) => [
            NotificationTemplatesActions.sendNotificationSuccess({ row }),
            NotificationTemplatesActions.loadHistory(),
            SharedActions.showMessage({
              success: this.translateService.instant('NOTIFICATION_TEMPLATES.TOASTS.SEND_SUCCESS'),
            }),
          ]),
          catchError((response) => [
            SharedActions.showMessage({ error: response?.error?.message }),
            NotificationTemplatesActions.sendNotificationFail({ error: response?.error?.message ?? null }),
          ]),
        );
      }),
      share(),
    ),
  );

  public loadTemplatePreview$ = createEffect(() =>
    this.actions$.pipe(
      ofType(NotificationTemplatesActions.loadTemplatePreview),
      switchMap(({ payload }) =>
        this.notificationService.previewNotificationTemplate(payload).pipe(
          switchMap((preview) => [
            NotificationTemplatesActions.loadTemplatePreviewSuccess({ preview }),
          ]),
          catchError((response) => [
            NotificationTemplatesActions.loadTemplatePreviewFail({
              error: response?.error?.message ?? null,
            }),
          ]),
        ),
      ),
      share(),
    ),
  );

  constructor(
    private actions$: Actions,
    private notificationService: NotificationService,
    private translateService: TranslateService,
  ) {}
}
